import { useState } from 'react';
import type { ReactNode } from 'react';
import { SUPPORT_SESSION_FRAGMENT_KEY } from '@yourstore/domain';
import { useAction, useApi, useConsole } from '../context';
import { Failure } from '../bits';
import { staffAppUrlFor } from '../../config';
import type { ApiFailure } from '../../api/errors';
import { useT } from '../../messages';

/**
 * The staff app's address with the credential in the **fragment** — never the
 * query string, and that is the entire design. A query string is sent to the
 * server and would persist the token verbatim in the load balancer's, Cloud
 * Run's and Hosting's request logs, the exact class of leak obligation K16
 * exists to prevent. A fragment never leaves the browser: the staff app reads
 * it once, scrubs the address bar, and holds it in that tab alone
 * (`apps/app`'s `auth/support.ts`). `SignIn.tsx`'s prefill uses the same
 * mechanism for the same reason, and the key is the domain's so both ends
 * agree on it.
 */
export function supportSessionUrl(staffAppUrl: string, token: string): string {
  return `${staffAppUrl}/#${SUPPORT_SESSION_FRAGMENT_KEY}=${encodeURIComponent(token)}`;
}

/**
 * **"See what they see"** (docs/status-2026-09-21.md §5; sixth pass, X5).
 *
 * One button, one look: `POST …/support-session` writes the audit row and
 * answers a fifteen-minute read-only seat; the console opens the staff app
 * with it in a new tab and keeps no copy. Three details carry the weight:
 *
 *  * **The tab is opened on the click, not after the answer.** A `window.open`
 *    that runs after an `await` is a popup a browser blocks, silently, and an
 *    operator would read that as "the button does nothing". So the blank tab
 *    is opened synchronously in the click handler and pointed at the app once
 *    the mint comes back — or closed if it does not.
 *  * **Nothing on this page holds the token.** It goes from the response into
 *    the new tab's address and is not put in state, not shown, not logged.
 *    What is shown is the audit id and the minute the look ends, which is the
 *    record and is what an operator can quote back.
 *  * **Read-only is the server's, not the button's.** The seat is `readonly`
 *    beside a marker `core-api` refuses every write on; the button's title says
 *    so because an operator should know what they are opening before they open
 *    it, not because the sentence is what enforces it.
 *
 * Since the redesign it is a header action rather than a card of its own, so
 * it is a hook: the header draws the button, and `SupportLookNotice` draws the
 * answer in the notice row under the header, where the stage move's answer
 * goes too. The staff app's address is the pattern with `{tenant}` filled in
 * (`staffAppUrlFor`).
 */
export interface SupportLook {
  look: () => void;
  busy: boolean;
  opened: { until: string; auditId: string } | null;
  failure: ApiFailure | null;
}

export function useSupportLook(tenantId: string): SupportLook {
  const api = useApi();
  const { config } = useConsole();
  const action = useAction();
  const [opened, setOpened] = useState<{ until: string; auditId: string } | null>(null);

  const look = (): void => {
    action.clear();
    setOpened(null);
    // Opened here, on the gesture — see the header.
    const tab = window.open('about:blank', '_blank');
    void action
      .run(async () => {
        const minted = await api.mintSupportSession(tenantId);
        const url = supportSessionUrl(
          staffAppUrlFor(config.staffAppUrlPattern, tenantId),
          minted.token,
        );
        if (tab === null) {
          // The browser refused the tab (a popup rule): fall back to this
          // one, which is a navigation the operator asked for.
          window.location.assign(url);
        } else {
          tab.location.href = url;
        }
        setOpened({ until: minted.expires_at, auditId: minted.audit_id });
      })
      .then((ok) => {
        if (!ok) tab?.close();
      });
  };

  return { look, busy: action.busy, opened, failure: action.failure };
}

export function SupportLookNotice({ state }: { state: SupportLook }): ReactNode {
  const t = useT();
  return (
    <>
      {state.opened !== null ? (
        <p className="ok">
          {t('support.opened', {
            until: state.opened.until.replace('T', ' ').slice(0, 16) + 'Z',
            auditId: state.opened.auditId,
          })}
        </p>
      ) : null}
      {state.failure !== null ? <Failure failure={state.failure} /> : null}
    </>
  );
}
